import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, Search, Building2, ChevronRight, TrendingUp,
  DollarSign, Target, RefreshCw, ArrowUpRight, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_COLORS = {
  sourced:          'bg-[#f5f5f5] text-[#4a4a4a]',
  initial_review:   'bg-[#d9e2c3] text-[#1a1a1a]',
  deep_dive:        'bg-[#ffd87a]100 text-[#ffd87a]700',
  ic_review:        'bg-violet-100 text-violet-700',
  term_sheet:       'bg-amber-100 text-amber-700',
  due_diligence:    'bg-orange-100 text-orange-700',
  final_approval:   'bg-emerald-100 text-emerald-700',
  closing:          'bg-teal-100 text-teal-700',
  closed:           'bg-green-100 text-green-700',
  passed:           'bg-[#c97a3a]/10 text-[#c97a3a]',
  lost:             'bg-[#c97a3a]/10 text-[#c97a3a]',
};

const STAGE_DOT = {
  sourced:          'bg-[#a8a8a8]',
  initial_review:   'bg-[#ffd87a]',
  deep_dive:        'bg-[#ffd87a]500',
  ic_review:        'bg-violet-500',
  term_sheet:       'bg-amber-500',
  due_diligence:    'bg-orange-500',
  final_approval:   'bg-emerald-500',
  closing:          'bg-teal-500',
  closed:           'bg-green-500',
  passed:           'bg-[#c97a3a]',
  lost:             'bg-[#c97a3a]',
};

const PRIORITY_COLORS = {
  low:    'text-[#4a4a4a]',
  medium: 'text-amber-500',
  high:   'text-orange-500',
  urgent: 'text-[#c97a3a]',
};

const PIPELINE_STAGES = [
  { key: 'sourced',        label: 'Sourced' },
  { key: 'initial_review', label: 'Initial Review' },
  { key: 'deep_dive',      label: 'Deep Dive' },
  { key: 'ic_review',      label: 'IC Review' },
  { key: 'term_sheet',     label: 'Term Sheet' },
  { key: 'due_diligence',  label: 'Due Diligence' },
  { key: 'closed',         label: 'Closed' },
];

const SECTORS = [
  'Technology', 'Healthcare', 'Fintech', 'Consumer', 'B2B SaaS',
  'Deep Tech', 'Climate / CleanTech', 'Real Estate', 'Media', 'Other',
];

// Deal Pipeline isn't startups-only — it also carries fund commitments, PE
// rounds, credit deals, etc. This is what actually distinguishes them.
const DEAL_CATEGORIES = [
  ['startup',            'Startup'],
  ['vc',                 'Venture Capital'],
  ['pe',                 'Private Equity'],
  ['search',             'Search Fund'],
  ['long_only',          'Long-Only'],
  ['credit',             'Private Credit'],
  ['infra',              'Infrastructure'],
  ['hedge_fund',         'Hedge Fund'],
  ['secondaries',        'Secondaries'],
  ['special_situations', 'Special Situations'],
  ['pre_ipo',            'Pre-IPO'],
  ['mezzanine',          'Mezzanine'],
  ['fof',                'Fund of Funds'],
  ['co_investment',      'Co-Investment'],
  ['royalty',            'Royalty / RBF'],
  ['natural_resources',  'Natural Resources'],
];
const DEAL_CATEGORY_LABELS = Object.fromEntries(DEAL_CATEGORIES);
const DEAL_CATEGORY_PALETTE = [
  'bg-[#dae4c4] text-[#1a1a1a]', 'bg-violet-100 text-violet-700', 'bg-amber-100 text-amber-700',
  'bg-sky-100 text-sky-700', 'bg-emerald-100 text-emerald-700', 'bg-orange-100 text-orange-700',
  'bg-teal-100 text-teal-700', 'bg-fuchsia-100 text-fuchsia-700', 'bg-rose-100 text-rose-700',
  'bg-indigo-100 text-indigo-700', 'bg-lime-100 text-lime-700', 'bg-cyan-100 text-cyan-700',
  'bg-pink-100 text-pink-700', 'bg-yellow-100 text-yellow-700', 'bg-slate-100 text-slate-700',
  'bg-red-100 text-red-700',
];
const DEAL_CATEGORY_COLORS = Object.fromEntries(
  DEAL_CATEGORIES.map(([key], i) => [key, DEAL_CATEGORY_PALETTE[i % DEAL_CATEGORY_PALETTE.length]])
);

const EMPTY_FORM = {
  deal_name: '', company_name: '', company_description: '', sector: '',
  deal_category: 'startup',
  stage: 'sourced', source: 'direct', priority: 'medium',
  target_investment_amount: '', pre_money_valuation: '', round_size: '',
  investment_currency: 'USD', thesis: '', notes: '',
};

export default function DealsPage({ embedded = false } = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStage, setSelectedStage] = useState(null);

  // New Deal dialog
  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => { fetchDeals(); }, []);

  const fetchDeals = async () => {
    try {
      const res = await axios.get(`${API}/deals/`);
      setDeals(res.data || []);
    } catch {
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDeal = async () => {
    if (!form.deal_name.trim() || !form.company_name.trim()) {
      toast.error('Deal name and company name are required');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        ...form,
        family_office_id: user?.family_office_id || '',
        target_investment_amount: form.target_investment_amount ? parseFloat(form.target_investment_amount) : null,
        pre_money_valuation: form.pre_money_valuation ? parseFloat(form.pre_money_valuation) : null,
        round_size: form.round_size ? parseFloat(form.round_size) : null,
      };
      const res = await axios.post(`${API}/deals/`, payload);
      toast.success('Deal created successfully');
      setShowNew(false);
      setForm(EMPTY_FORM);
      navigate(`/deals/${res.data.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to create deal';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setCreating(false);
    }
  };

  const fmt = (v) => {
    if (!v) return '—';
    if (v >= 1e9) return `$${(v/1e9).toFixed(1)}B`;
    if (v >= 1e6) return `$${(v/1e6).toFixed(1)}M`;
    if (v >= 1e3) return `$${(v/1e3).toFixed(0)}K`;
    return `$${v}`;
  };

  const stageLabel = (key) => PIPELINE_STAGES.find(s => s.key === key)?.label || key;

  // Closed deals have left the pipeline (they're tracked as actual holdings
  // elsewhere) — Deal Pipeline only shows deals still in progress.
  const pipelineDeals = deals.filter(d => d.stage !== 'closed');
  const FUNNEL_STAGES = PIPELINE_STAGES.filter(s => s.key !== 'closed');

  const filtered = pipelineDeals.filter(d => {
    const q = searchTerm.toLowerCase();
    const matchSearch = d.deal_name?.toLowerCase().includes(q) || d.company_name?.toLowerCase().includes(q);
    const matchStage = !selectedStage || d.stage === selectedStage;
    return matchSearch && matchStage;
  });

  const byStage = FUNNEL_STAGES.reduce((acc, s) => {
    acc[s.key] = pipelineDeals.filter(d => d.stage === s.key);
    return acc;
  }, {});

  const totalPipeline = pipelineDeals.reduce((s, d) => s + (d.target_investment_amount || 0), 0);
  const activeDeals = pipelineDeals.filter(d => !['passed','lost'].includes(d.stage)).length;
  const highPriority = pipelineDeals.filter(d => d.priority === 'high' || d.priority === 'urgent').length;

  return (
    <div data-testid="deals-page" className={embedded ? '' : 'p-6 min-h-screen'}>
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#dae4c4] flex items-center justify-center shadow-md">
              <Building2 className="w-5 h-5 text-[#1a1a1a]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#1a1a1a]">Deal Pipeline</h1>
              <p className="text-sm text-[#4a4a4a]">Track deals from sourcing to close</p>
            </div>
          </div>
          <Button className="bg-[#ffd879] hover:bg-[#f5cc6a] text-black" onClick={() => setShowNew(true)}>
            <Plus className="w-4 h-4 mr-2" /> New Deal
          </Button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-4 text-center">
          <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">Total Deals</p>
          <p className="text-3xl font-bold text-[#1a1a1a]">{pipelineDeals.length}</p>
        </div>
        <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-4 text-center">
          <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">Active</p>
          <p className="text-3xl font-bold text-[#1a1a1a]">{activeDeals}</p>
        </div>
        <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-4 text-center">
          <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">High Priority</p>
          <p className="text-3xl font-bold text-[#ffd87a]">{highPriority}</p>
        </div>
        <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-4 text-center">
          <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">Pipeline Value</p>
          <p className="text-2xl font-bold text-[#1a1a1a]">{fmt(totalPipeline)}</p>
        </div>
      </div>

      {/* Pipeline Stage Funnel */}
      <div className={`grid ${embedded ? 'grid-cols-3' : 'grid-cols-3 md:grid-cols-6'} gap-2 mb-6`}>
        {FUNNEL_STAGES.map(s => (
          <button
            key={s.key}
            onClick={() => setSelectedStage(selectedStage === s.key ? null : s.key)}
            className={`rounded-xl p-3 text-center transition-all border ${
              selectedStage === s.key
                ? 'border-[#1a1a1a] bg-[#1a1a1a] text-white shadow-md'
                : 'border-[#e5e5e5] bg-white hover:border-[#e5e5e5] hover:bg-[#f9f9f9]'
            }`}
          >
            <div className={`text-2xl font-bold mb-0.5 ${selectedStage === s.key ? 'text-white' : 'text-[#1a1a1a]'}`}>
              {byStage[s.key]?.length || 0}
            </div>
            <div className={`text-xs leading-tight ${selectedStage === s.key ? 'text-[#ffd87a]' : 'text-[#4a4a4a]'}`}>
              {s.label}
            </div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a4a4a]" />
        <Input
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search by deal or company name…"
          className="pl-10 bg-white border-[#e5e5e5]"
        />
      </div>

      {/* Deals Table */}
      <div className="overflow-hidden border border-[#e5e5e5] shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-16 bg-white">
            <RefreshCw className="w-8 h-8 text-[#ffd87a] animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white">
            <Building2 className="w-12 h-12 text-[#4a4a4a] mx-auto mb-3" />
            <p className="text-[#4a4a4a]">No deals found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-[#dae4c4]">
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Deal / Company</th>
                <th className="text-left px-4 py-3.5 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Sector</th>
                <th className="text-left px-4 py-3.5 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Stage</th>
                <th className="text-right px-4 py-3.5 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Target</th>
                <th className="text-right px-4 py-3.5 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Valuation</th>
                <th className="text-center px-4 py-3.5 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Priority</th>
                <th className="px-4 py-3.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e5e5e5]">
              {filtered.map((deal, idx) => (
                <tr
                  key={deal.id}
                  className={`transition-colors hover:bg-[#f9f9f9] ${idx % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'}`}
                  data-testid={`deal-${deal.id}`}
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-10 rounded-full shrink-0 ${STAGE_DOT[deal.stage] || 'bg-[#e5e5e5]'}`} />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <p className="font-semibold text-[#1a1a1a]">{deal.deal_name}</p>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${DEAL_CATEGORY_COLORS[deal.deal_category] || DEAL_CATEGORY_COLORS.other}`}>
                            {DEAL_CATEGORY_LABELS[deal.deal_category] || 'Startup'}
                          </span>
                        </div>
                        <p className="text-xs text-[#4a4a4a]">{deal.company_name}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-[#4a4a4a]">{deal.sector || '—'}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STAGE_COLORS[deal.stage] || 'bg-[#f5f5f5] text-[#4a4a4a]'}`}>
                      {stageLabel(deal.stage)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right font-semibold text-[#1a1a1a]">{fmt(deal.target_investment_amount)}</td>
                  <td className="px-4 py-4 text-right text-sm text-[#4a4a4a]">{fmt(deal.pre_money_valuation)}</td>
                  <td className="px-4 py-4 text-center">
                    <span className={`text-xs font-bold uppercase tracking-wide ${PRIORITY_COLORS[deal.priority] || 'text-[#4a4a4a]'}`}>
                      {deal.priority}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <Link to={`/deals/${deal.id}`} className="p-1.5 rounded-lg hover:bg-[#f5f5f5] text-[#4a4a4a] hover:text-[#1a1a1a] transition-colors inline-flex">
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── New Deal Dialog ── */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#1a1a1a]" />
              New Deal
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Row 1 */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1 block">Deal Name *</label>
                <Input value={form.deal_name} onChange={e => setField('deal_name', e.target.value)}
                  placeholder="e.g. Series A — Acme Corp" className="text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1 block">Company Name *</label>
                <Input value={form.company_name} onChange={e => setField('company_name', e.target.value)}
                  placeholder="e.g. Acme Corp" className="text-sm" />
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1 block">Deal Type</label>
                <Select value={form.deal_category} onValueChange={v => setField('deal_category', v)}>
                  <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DEAL_CATEGORIES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1 block">Sector</label>
                <Select value={form.sector} onValueChange={v => setField('sector', v)}>
                  <SelectTrigger className="text-sm"><SelectValue placeholder="Select sector" /></SelectTrigger>
                  <SelectContent>{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1 block">Stage</label>
                <Select value={form.stage} onValueChange={v => setField('stage', v)}>
                  <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PIPELINE_STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1 block">Priority</label>
                <Select value={form.priority} onValueChange={v => setField('priority', v)}>
                  <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['low','medium','high','urgent'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 3 — Financials */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1 block">Target Investment ($)</label>
                <Input value={form.target_investment_amount} onChange={e => setField('target_investment_amount', e.target.value)}
                  placeholder="e.g. 5000000" type="number" className="text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1 block">Pre-Money Valuation ($)</label>
                <Input value={form.pre_money_valuation} onChange={e => setField('pre_money_valuation', e.target.value)}
                  placeholder="e.g. 20000000" type="number" className="text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1 block">Round Size ($)</label>
                <Input value={form.round_size} onChange={e => setField('round_size', e.target.value)}
                  placeholder="e.g. 10000000" type="number" className="text-sm" />
              </div>
            </div>

            {/* Row 4 — Source */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1 block">Source</label>
                <Select value={form.source} onValueChange={v => setField('source', v)}>
                  <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[['direct','Direct'],['referral','Referral'],['vc_fund','VC Fund'],
                      ['accelerator','Accelerator'],['conference','Conference'],
                      ['cold_outreach','Cold Outreach'],['other','Other']].map(([k,l]) =>
                      <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1 block">Company Description</label>
                <Input value={form.company_description} onChange={e => setField('company_description', e.target.value)}
                  placeholder="One-line description" className="text-sm" />
              </div>
            </div>

            {/* Thesis */}
            <div>
              <label className="text-xs font-medium text-[#4a4a4a] mb-1 block">Investment Thesis</label>
              <textarea
                value={form.thesis}
                onChange={e => setField('thesis', e.target.value)}
                placeholder="Why is this deal attractive?"
                rows={3}
                className="w-full border border-[#e5e5e5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffd87a] resize-none"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNew(false); setForm(EMPTY_FORM); }}>Cancel</Button>
            <Button onClick={handleCreateDeal} disabled={creating} className="bg-[#ffd879] hover:bg-[#f5cc6a] text-black">
              {creating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Creating…</> : <><Plus className="w-4 h-4 mr-1.5" />Create Deal</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

